import { createContext } from 'preact';
import { useCallback, useContext, useEffect, useMemo, useState } from 'preact/hooks';
import type { ComponentChildren } from 'preact';
import {
  ApiError,
  UNAUTHORIZED_EVENT,
  api,
  clearSession,
  loadSession,
  saveSession,
} from './api.js';
import type { LoginResponse, Role, SessionPlayer } from './types.js';

const DASHBOARD_ROLES: Role[] = ['admin', 'owner'];

export function canUseDashboard(role: Role): boolean {
  return DASHBOARD_ROLES.includes(role);
}

interface AuthState {
  player: SessionPlayer | null;
  token: string | null;
  /** True once a stored session was found but its role can't use the dashboard. */
  forbidden: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ComponentChildren }) {
  const initial = loadSession();
  const [player, setPlayer] = useState<SessionPlayer | null>(
    initial && canUseDashboard(initial.player.role as Role)
      ? (initial.player as SessionPlayer)
      : null,
  );
  const [token, setToken] = useState<string | null>(
    initial && canUseDashboard(initial.player.role as Role) ? initial.token : null,
  );
  const [forbidden, setForbidden] = useState(
    !!initial && !canUseDashboard(initial.player.role as Role),
  );

  const logout = useCallback(() => {
    clearSession();
    setPlayer(null);
    setToken(null);
    setForbidden(false);
  }, []);

  useEffect(() => {
    const onUnauthorized = () => logout();
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
  }, [logout]);

  const login = useCallback(async (email: string, password: string) => {
    const res = await api.post<LoginResponse>('/api/auth/login', { email, password }, true);
    if (!canUseDashboard(res.player.role)) {
      setForbidden(true);
      setPlayer(null);
      setToken(null);
      throw new ApiError(403, `${res.player.role} accounts cannot open the admin dashboard.`);
    }
    saveSession({ token: res.token, player: res.player });
    setPlayer(res.player);
    setToken(res.token);
    setForbidden(false);
  }, []);

  const value = useMemo<AuthState>(
    () => ({ player, token, forbidden, login, logout }),
    [player, token, forbidden, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth() outside <AuthProvider>');
  return ctx;
}
